import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  PackageCheck,
  Search,
  Tag,
  TicketPercent,
  X,
} from "lucide-react";
import DateRangePicker from "../../../ecommerce/globalcomponents/DateRangePicker";
import { offersData } from "./offersData";
import {
  formatCurrency,
  formatStuffDate,
  formatStuffLongDate,
  toClassSlug,
  OfferRecord,
  OfferStatus,
} from "./stuffAdapters";
import "./OffersPage.css";

const renderStatus = (status: OfferStatus) => (
  <span className={`offers-status offers-status-${toClassSlug(status)}`}>
    {status}
  </span>
);

const Offers = () => {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const filteredOffers = offersData.filter((offer) => {
    const term = searchValue.trim().toLowerCase();
    const matchesSearch =
      !term ||
      offer.productName.toLowerCase().includes(term) ||
      offer.sellerStoreName.toLowerCase().includes(term) ||
      offer.category.toLowerCase().includes(term);
    const matchesFrom = !dateRange.from || offer.date >= dateRange.from;
    const matchesTo = !dateRange.to || offer.date <= dateRange.to;
    return matchesSearch && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOffers.length / pageSize),
  );
  const page = Math.min(currentPage, totalPages);
  const visibleOffers = filteredOffers.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const renderMobileCard = (offer: OfferRecord) => (
    <button
      type="button"
      className="offers-mobile-card"
      key={offer.id}
      onClick={() => navigate("/OfferDetails")}
    >
      <span className="offers-mobile-serial">{offer.serialNo}</span>
      <div className="offers-mobile-image">
        <img src={offer.productImage} alt={offer.productName} />
      </div>
      <div className="offers-mobile-copy">
        <div className="offers-mobile-topline">
          {renderStatus(offer.status)}
          <span>
            <CalendarDays size={13} strokeWidth={2} />
            {formatStuffLongDate(offer.date)}
          </span>
        </div>
        <h2>{offer.productName}</h2>
        <p>{offer.description}</p>
        <strong className="offers-mobile-price">
          {formatCurrency(offer.price)}
        </strong>
      </div>
      <ChevronRight
        className="offers-mobile-chevron"
        size={20}
        strokeWidth={2}
      />
    </button>
  );

  return (
    <section className="offers-page">
      <div className="offers-shell">
        <header className="offers-header">
          <div className="offers-heading">
            <span className="offers-heading-icon">
              <TicketPercent size={22} strokeWidth={2} />
            </span>
            <div>
              <span className="offers-kicker">MY STUFF</span>
              <h1>Offers</h1>
              <p>
                Browse purchase offers, discounts, seller information and
                expiry details.
              </p>
            </div>
          </div>
          <span className="offers-count-chip">
            {filteredOffers.length} offers
          </span>
        </header>

        <div className="offers-toolbar">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Offers per page"
          >
            <option value={5}>5 Entries</option>
            <option value={10}>10 Entries</option>
            <option value={20}>20 Entries</option>
          </select>

          <div className="offers-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div className="offers-search">
            <Search size={17} strokeWidth={2} />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search offers..."
              aria-label="Search offers"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="offers-desktop-list">
          {visibleOffers.length > 0 ? (
            visibleOffers.map((offer) => (
              <article
                className="offers-desktop-card"
                key={offer.id}
                onClick={() => navigate("/OfferDetails")}
                style={{ cursor: "pointer" }}
              >
                <div className="offers-desktop-meta">
                  <div>
                    <span>DATE</span>
                    <strong>{formatStuffDate(offer.date)}</strong>
                  </div>
                  <div>
                    <span>Purchase Type</span>
                    <strong>{offer.purchaseType}</strong>
                  </div>
                  <div>
                    <span>Seller Store Name</span>
                    <strong>{offer.sellerStoreName}</strong>
                  </div>
                  <div>
                    <span>Seller Store ID</span>
                    <strong>{offer.sellerStoreId}</strong>
                  </div>
                  <div>
                    <span>Category</span>
                    <strong>{offer.category}</strong>
                  </div>
                  <div className="offers-desktop-status-block">
                    <span>STATUS</span>
                    {renderStatus(offer.status)}
                  </div>
                </div>

                <div className="offers-desktop-product">
                  <div className="offers-desktop-product-image">
                    <img src={offer.productImage} alt={offer.productName} />
                  </div>
                  <div className="offers-desktop-product-copy">
                    <span>FEATURED OFFER</span>
                    <h2>{offer.productName}</h2>
                    <p>{offer.description}</p>
                    <div className="offers-desktop-price">
                      <span>Price</span>
                      <strong>{formatCurrency(offer.price)}</strong>
                    </div>
                  </div>
                </div>

                <div className="offers-desktop-footer">
                  <div>
                    <Tag size={15} strokeWidth={2} />
                    <span>Discount</span>
                    <strong>{offer.discountPercent}%</strong>
                  </div>
                  <div>
                    <Clock3 size={15} strokeWidth={2} />
                    <span>Expires In</span>
                    <strong>{offer.expiresIn}</strong>
                  </div>
                  <div>
                    <PackageCheck size={15} strokeWidth={2} />
                    <span>Quantity</span>
                    <strong>{offer.quantity} item</strong>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="offers-desktop-empty">
              <TicketPercent size={34} strokeWidth={1.8} />
              <strong>No offers found</strong>
              <span>Try another search or date range.</span>
            </div>
          )}
        </div>

        <div className="offers-mobile-list">
          {visibleOffers.length > 0 ? (
            visibleOffers.map(renderMobileCard)
          ) : (
            <div className="offers-mobile-empty">
              <TicketPercent size={32} strokeWidth={1.8} />
              <strong>No offers found</strong>
              <span>Try another search or date range.</span>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <footer className="offers-pagination">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setCurrentPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronRight
                size={18}
                style={{ transform: "rotate(180deg)" }}
              />
            </button>
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setCurrentPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </footer>
        )}
      </div>
    </section>
  );
};

export default Offers;
